// libs
import React, { memo } from 'react';

// hook
import useTheme from '@nykaa/ui-components/themes/hooks/useTheme';

// components
import StarRating from 'shared/components/StarRating';
import RatingBars from './components/RatingBars';

// interface
import { IRatingSummary } from 'shared/layouts/DetailsPage/types';

import { Theme } from '@nykaa/ui-components/themes/types';

// styles
import {
  SummaryContainer,
  RatingCount,
  BasedOnDark,
  RatingBarOuterContainer,
  RatingContainer,
  PlaceholderRow,
} from './styles';

interface IReviewSummaryProps {
  aggregated: string;
  ratingSummary: IRatingSummary[];
  totalCount: number;
  isFetching: boolean;
  isMobile?: boolean;
}

const MAX_RATING_COUNT = 5;
const basedOn = (count: number) => `Based on ${count} Ratings`;

function ReviewSummary({
  aggregated,
  ratingSummary,
  totalCount,
  isFetching,
  isMobile,
}: IReviewSummaryProps) {
  const aggregatedValue = Number(aggregated);
  const theme: Theme = useTheme();

  if (isFetching) {
    // Placeholder while fetching the data
    return (
      <SummaryContainer>
        <RatingContainer>
          <PlaceholderRow className="starRating" />
          <PlaceholderRow className="ratingCount" />
          <PlaceholderRow className="basedOn" />
        </RatingContainer>
        <RatingBarOuterContainer>
          <PlaceholderRow />
          <PlaceholderRow />
          <PlaceholderRow />
          <PlaceholderRow />
        </RatingBarOuterContainer>
      </SummaryContainer>
    );
  }

  if (aggregatedValue === 0 || isNaN(aggregatedValue)) {
    return null;
  }

  return (
    <SummaryContainer>
      <RatingContainer>
        <StarRating
          activeCount={aggregatedValue}
          size="24px"
          activeColor={theme.colors.textPrimary}
          className="stars"
        />
        <RatingCount>
          {aggregatedValue.toFixed(1)}
          <span className="maxCount">/{MAX_RATING_COUNT}</span>
        </RatingCount>

        {totalCount && <BasedOnDark>{basedOn(totalCount)}</BasedOnDark>}
      </RatingContainer>

      <RatingBarOuterContainer>
        <RatingBars ratingSummary={ratingSummary} isMobile={isMobile} />
      </RatingBarOuterContainer>
    </SummaryContainer>
  );
}

export default memo(ReviewSummary);
